// React hook imports
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Custom hook imports
import { useNavigateWithSameKey } from "../../../../../../../custom-hooks/navigateWithKey";

// Redux imports
import { useDispatch, useSelector } from "react-redux";
import {
  createEnvironmentData,
  resetEnvironmentData,
  setBrowserState,
  setEnvTypeState,
  setOperatingSystemState,
  setPasswordState,
  setUrlState,
  setUsernameState,
} from "../../../../../../../store/slices/test-automation/web/flow-stages/environmentSlice";

// MUI components and icons imports
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2,
  Container,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Refresh as ResetIcon,
  ArrowForward as NextIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

// Local images imports
import { ReactComponent as ChromeIcon } from "../../../../../../../images/test-automation/browser_chromeIcon.svg";
import { ReactComponent as EdgeIcon } from "../../../../../../../images/test-automation/browser_EdgeIcon.svg";
import { ReactComponent as FirefoxIcon } from "../../../../../../../images/test-automation/browser_FirefoxIcon.svg";
import { ReactComponent as WindowsIcon } from "../../../../../../../images/test-automation/operating_system_Windows8Icon.svg";
import { ReactComponent as MacIcon } from "../../../../../../../images/test-automation/operating_system_appleIcon.svg";
import { ReactComponent as LinuxIcon } from "../../../../../../../images/test-automation/operating_system_LinuxIcon.svg";

// Main function return React Component
const SetTestEnvironmentPage = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  // Capture automationType and locationKey from state
  const { automationType, locationKey } = location.state || {};

  // Capture data from redux store using selector
  const preferences = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "automationPreferences"
      ] || {}
  );
  const environment = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "environmentData"
      ] || {}
  );

  // Defining local state variables
  const [showPassword, setShowPassword] = useState(false);
  const [urlError, setUrlError] = useState("");

  // Function to be run when locationKey and environment are updated
  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }

    if (Object.keys(environment).length === 0) {
      dispatch(createEnvironmentData(locationKey));
    }
  }, [locationKey, environment]);

  // URL validation function
  const validateUrl = (url) => {
    if (!url) {
      setUrlError("URL is required");
      return false;
    }

    // URL pattern validation for http/https with valid domain
    const urlPattern =
      /^https?:\/\/(?:[-\w.])+(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?$/;

    if (!urlPattern.test(url)) {
      setUrlError(
        "Please enter a valid URL (e.g., https://example.com or http://example.co.in)"
      );
      return false;
    }

    setUrlError("");
    return true;
  };

  // Array of objects, each object representing a browser
  const browsers = [
    {
      id: "chrome",
      name: "Chrome",
      icon: <ChromeIcon />,
    },
    {
      id: "edge",
      name: "Edge",
      icon: <EdgeIcon />,
    },
    {
      id: "firefox",
      name: "FireFox",
      icon: <FirefoxIcon />,
    },
  ];

  // Array of objects, each object representing an os
  const operatingSystems = [
    {
      id: "windows",
      name: "Windows",
      icon: <WindowsIcon />,
    },
    {
      id: "mac",
      name: "Mac",
      icon: <MacIcon />,
    },
    {
      id: "linux",
      name: "Linux",
      icon: <LinuxIcon />,
    },
  ];

  const handleBrowserSelect = (browserId) => {
    // Getting action object using setBrowserState and executing action using dispatch
    dispatch(setBrowserState(locationKey, browserId));
  };

  const handleOSSelect = (osId) => {
    // Getting action object using setOperatingSystemState and executing action using dispatch
    dispatch(setOperatingSystemState(locationKey, osId));
  };

  // Handle function for all input field updates
  const handleInputChange = (field, value) => {
    // Switch condition for the updated field
    // Getting action object using respective setMethods and executing action using dispatch
    switch (field) {
      case "url":
        dispatch(setUrlState(locationKey, value));
        validateUrl(value);
        break;

      case "username":
        dispatch(setUsernameState(locationKey, value));
        break;

      case "password":
        dispatch(setPasswordState(locationKey, value));
        break;

      default:
        break;
    }
  };

  const handleNext = () => {
    // Validate URL before proceeding
    if (!validateUrl(environment.url)) {
      return;
    }

    // Navigating to test case upload page
    navigate("/dashboard/test-automation/test-cases", {
        automationType
    });
  };

  // Navigating back to one step in history
  const handleBack = () => {
    navigate(-1);
  };

  // Handle function to reset environment data
  const handleReset = () => {
    // Getting action object using resetEnvironmentData and executing action using dispatch
    dispatch(resetEnvironmentData(locationKey));
  };

  const renderBrowserCard = (browser, isSelected) => (
    <Card
      key={browser.id}
      sx={{
        cursor: "pointer",
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#1976d2",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
        backgroundColor: isSelected ? "#e6f2ff" : "white",
        width: "350px",
        height: "106px",
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
      onClick={() => handleBrowserSelect(browser.id)}
    >
      <CardContent
        sx={{
          p: "16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "16px",
          pt: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          {browser.icon}
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Manrope",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: "16px",
            color: "#000000",
            textAlign: "left",
          }}
        >
          {browser.name}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderOSCard = (os, isSelected) => (
    <Card
      key={os.id}
      sx={{
        cursor: "pointer",
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#1976d2",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
        backgroundColor: isSelected ? "#e6f2ff" : "white",
        width: "350px",
        height: "106px",
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
      onClick={() => handleOSSelect(os.id)}
    >
      <CardContent
        sx={{
          p: "16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "16px",
          pt: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          {os.icon}
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Manrope",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: "16px",
            color: "#000000",
            textAlign: "left",
          }}
        >
          {os.name}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* White Content Area */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            p: 4,
            flex: 1,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mb: 4,
              fontWeight: "bold",
              color: "#333",
              fontSize: "28px",
              fontFamily: "Manrope",
            }}
          >
            Set Test Environment
          </Typography>

          {/* Browser Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontSize: "16px",
                fontFamily: "Manrope",
              }}
            >
              Browser
            </Typography>
            <Grid2
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(300px, 1fr))",
                  md: "repeat(auto-fit, minmax(300px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {browsers.map((browser) => (
                <Grid2 item key={browser.id} xs={4}>
                  {renderBrowserCard(
                    browser,
                    environment.browser === browser.id
                  )}
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Operating System Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontSize: "16px",
                fontFamily: "Manrope",
              }}
            >
              Operating System
            </Typography>
            <Grid2
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(300px, 1fr))",
                  md: "repeat(auto-fit, minmax(300px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {operatingSystems.map((os) => (
                <Grid2 item key={os.id} xs={4}>
                  {renderOSCard(os, environment.operatingSystem === os.id)}
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* AUT (Application Under Test) Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontSize: "16px",
                fontFamily: "Manrope",
              }}
            >
              AUT
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: "#666",
                fontSize: "14px",
                fontFamily: "Manrope",
              }}
            >
              Testing credentials for your web site to test functionality which
              requires user to login. (Username and password are optional)
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr", // Single column on mobile
                    md: "1fr 3fr", // 1:3 ratio on medium+ (equivalent to 4:12)
                  },
                  gap: 3,
                }}
              >
                <FormControl>
                  <InputLabel id="demo-simple-select-label">
                    Env Type
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={environment.envType}
                    label="Env Type"
                    onChange={(e) => {
                      dispatch(setEnvTypeState(locationKey, e.target.value));
                    }}
                  >
                    <MenuItem value="Development">Dev</MenuItem>
                    <MenuItem value="QA">QA</MenuItem>
                    <MenuItem value="Staging">Staging</MenuItem>
                    <MenuItem value="Production">Production</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Enter URL"
                  placeholder="https://example.com"
                  value={environment.url}
                  onChange={(e) => handleInputChange("url", e.target.value)}
                  required
                  error={!!urlError}
                  helperText={urlError}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px",
                    },
                  }}
                />
              </Box>

              {/* Username and Password Fields - Side by Side */}
              <Box sx={{ display: "flex", gap: 3 }}>
                <TextField
                  fullWidth
                  label="Username"
                  placeholder="Enter Username"
                  value={environment.username}
                  onChange={(e) =>
                    handleInputChange("username", e.target.value)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px",
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  placeholder="Enter Password"
                  type={showPassword ? "text" : "password"}
                  value={environment.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer Area */}
        <Box
          sx={{
            // p: 4,
            mt: 4,
            backgroundColor: "transparent",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Left: Back Button */}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              sx={{
                // width: "120px",
                // height: "40px",
                borderColor: "#0094FB",
                color: "#0094FB",
                textTransform: "none",
                fontFamily: "Manrope",
                fontWeight: 500,
                borderRadius: "8px",
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                // gap: "8px",
                "&:hover": {
                  borderColor: "#0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Back
            </Button>

            {/* Right: Reset and Confirm Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ResetIcon />}
                onClick={handleReset}
                sx={{
                  // width: "199px",
                  // height: "40px",
                  borderColor: "#0094FB",
                  color: "#0094FB",
                  textTransform: "none",
                  fontFamily: "Manrope",
                  fontWeight: 500,
                  borderRadius: "8px",
                  borderWidth: "2px",
                  paddingTop: "8px",
                  paddingRight: "16px",
                  paddingBottom: "8px",
                  paddingLeft: "16px",
                  // gap: "8px",
                  "&:hover": {
                    borderColor: "#0094FB",
                    backgroundColor: "rgba(0, 148, 251, 0.04)",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                disabled={
                  !environment.browser ||
                  !environment.operatingSystem ||
                  !environment.url ||
                  !!urlError
                }
                sx={{
                  // width: "199px",
                  // height: "40px",
                  backgroundColor: "#0094FB",
                  color: "white",
                  textTransform: "none",
                  fontFamily: "Manrope",
                  fontWeight: 500,
                  borderRadius: "8px",
                  borderWidth: "2px",
                  borderColor: "#0094FB",
                  paddingTop: "8px",
                  paddingRight: "16px",
                  paddingBottom: "8px",
                  paddingLeft: "16px",
                  // gap: "8px",
                  "&:hover": {
                    backgroundColor: "#0078D4",
                    borderColor: "#0078D4",
                  },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                    borderColor: "#ccc",
                  },
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SetTestEnvironmentPage;
